/**
 * ModelFamiliesPanel — the per-tenant "AI Models" manager.
 *
 * Replaces the old per-version AllowedModelsPanel. One row per model
 * FAMILY (Sonnet, GPT Mini, Flash…), grouped by provider:
 *   - ON/OFF switch  → tenant-level family enable (what users can pick),
 *   - version select → which concrete release the family runs for this
 *     tenant (users never see this choice — they only see the family).
 * Rows show an "override" badge whenever the tenant diverges from the
 * platform default, with one-click revert.
 *
 * Cascade toolbar (CascadeControls): apply-to-sub-tenants, enforce
 * toggle, drift-vs-parent badges — the 'models' domain.
 *
 * Data:
 *   GET   /super-admin/tenants/<id>/model-families
 *   PATCH /super-admin/tenants/<id>/model-families   (full replace of overrides)
 *
 * Save semantics: overrides are a diff vs platform defaults — a family
 * appears in the payload only when its version or enabled state differs
 * from the platform. Auto-saves on every change.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Switch, Select, Button, Spin, Tag, Tooltip, Modal, message as antdMessage, Empty } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { CascadeControls, CascadeStatus } from './CascadeControls';
import { ModelChangeImpact, ModelImpact, impactIsEmpty } from './ModelChangeImpact';
import { bidiIsolate } from '@/utils/bidi';

interface FamilyVersion {
  id: string;
  name: string;
  /** Kept for existing pins only (GPT's Sol / Terra releases, which GPT Sol
   *  / GPT Terra now offer): listed only while it is the family's pin. */
  legacy?: boolean;
}

interface FamilyRow {
  key: string;
  name: string;
  provider: string;
  provider_name: string;
  description: string;
  platform_default_version: string;
  platform_enabled: boolean;
  version: string;
  version_name: string;
  enabled: boolean;
  is_version_override: boolean;
  is_enabled_override: boolean;
  versions: FamilyVersion[];
  /** What a USER of this tenant can pick, computed by the same backend
   *  rule the agent-edit picker uses. The switch is that rule. */
  selectable: boolean;
  hidden_reason: 'disabled' | null;
  /** Switched off, and its agents run this family instead (GPT -> GPT Terra). */
  hands_over_to?: string | null;
}

interface ModelFamiliesPanelProps {
  tenantId: string;
  /** Notify the parent page after any change that can move the tenant-tree
   *  badges (enforcement / differs-from-parent), so they refresh instead of
   *  showing yesterday's state until the tab is re-entered. */
  onCascadeChanged?: () => void;
  /** Render without the outer Card (Tenant Tree pane already provides one). */
  bare?: boolean;
}

export function ModelFamiliesPanel({ tenantId, bare = false, onCascadeChanged }: ModelFamiliesPanelProps) {
  const { t } = useLanguage();
  const [families, setFamilies] = useState<FamilyRow[]>([]);
  const [selectableFamilies, setSelectableFamilies] = useState<string[]>([]);
  const [cascade, setCascade] = useState<CascadeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null); // family key being saved
  // Always-current families snapshot. The PATCH is a FULL REPLACE of the
  // tenant's overrides, so building it from a stale `families` closure
  // (two quick edits inside one render cycle) would silently revert the
  // first edit. Row mutations read from and write to this ref
  // synchronously, never the (async) state closure.
  const familiesRef = useRef<FamilyRow[]>([]);
  // Serialize saves: a second edit made while a PATCH is in flight waits
  // for it, so full-replace payloads always include every prior edit.
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const pendingSavesRef = useRef(0);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/super-admin/tenants/${tenantId}/model-families`);
      familiesRef.current = res.data?.families || [];
      setFamilies(familiesRef.current);
      setSelectableFamilies(res.data?.selectable_families || []);
      setCascade(res.data?.cascade || null);
    } catch (err: any) {
      antdMessage.error(err?.response?.data?.error || `Failed to load model families: ${err?.message || err}`);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const out: Array<{ provider: string; provider_name: string; rows: FamilyRow[] }> = [];
    const seen: Record<string, number> = {};
    for (const row of families) {
      if (!(row.provider in seen)) {
        seen[row.provider] = out.length;
        out.push({ provider: row.provider, provider_name: row.provider_name, rows: [] });
      }
      out[seen[row.provider]].rows.push(row);
    }
    return out;
  }, [families]);

  /** Build the diff-vs-platform overrides payload from a families array. */
  function buildOverrides(rows: FamilyRow[]): Record<string, { version?: string; enabled?: boolean }> {
    const out: Record<string, { version?: string; enabled?: boolean }> = {};
    for (const row of rows) {
      const entry: { version?: string; enabled?: boolean } = {};
      if (row.version !== row.platform_default_version) entry.version = row.version;
      if (row.enabled !== row.platform_enabled) entry.enabled = row.enabled;
      if (Object.keys(entry).length > 0) out[row.key] = entry;
    }
    return out;
  }

  function persist(mutate: (rows: FamilyRow[]) => FamilyRow[], savedKey: string, confirmBreaking = false) {
    // Apply the row change optimistically from the CURRENT snapshot —
    // synchronously through the ref, so a second edit in the same render
    // cycle builds on top of this one instead of reverting it.
    const previous = familiesRef.current;
    const nextFamilies = mutate(previous);
    familiesRef.current = nextFamilies;
    setSaving(savedKey);
    setFamilies(nextFamilies);
    pendingSavesRef.current += 1;
    // Queue the PATCH behind any in-flight save. The payload is rebuilt
    // from the latest snapshot at send time, so it carries every edit
    // made while earlier saves were still on the wire.
    saveChainRef.current = saveChainRef.current.then(async () => {
      try {
        const res = await api.patch(`/super-admin/tenants/${tenantId}/model-families`, {
          model_families: buildOverrides(familiesRef.current),
          ...(confirmBreaking ? { confirm_breaking: true } : {}),
        });
        const cleared: string[] = res.data?.cleared_ancestor_enforcement || [];
        const pushed: number = res.data?.descendants_updated || 0;
        if (cleared.length > 0) {
          antdMessage.warning(
            t('superAdmin.modelFamilies.clearedAncestors') ||
            t('superAdmin.nowDiffersFromParent')
          );
        }
        if (pushed > 0) {
          antdMessage.info(
            (t('superAdmin.modelFamilies.pushedToDescendants') || 'Change pushed to {count} sub-tenant(s) (enforcement is on).')
              .replace('{count}', String(pushed))
          );
        }
        // Refresh badges (override markers, cascade drift) from the source
        // of truth — but only once the queue drains, so a mid-chain reload
        // can't clobber a newer optimistic edit that is still queued.
        if (pendingSavesRef.current === 1) {
          await load();
          onCascadeChanged?.();
        }
      } catch (err: any) {
        familiesRef.current = previous;
        setFamilies(previous);
        antdMessage.error(err?.response?.data?.error || `Save failed: ${err?.message || err}`);
      } finally {
        pendingSavesRef.current -= 1;
        if (pendingSavesRef.current === 0) setSaving(null);
      }
    });
  }

  /**
   * Save a row change - after asking the server who it moves (the impact
   * check: which agents run another release, meta-agent slots, agents left
   * with no model, what the new release changes for them). A change that
   * moves nobody is saved at once; otherwise the super admin sees the list
   * and confirms. Agents left with no model are confirmed explicitly (the
   * server refuses them otherwise).
   */
  async function checkThenPersist(mutate: (rows: FamilyRow[]) => FamilyRow[], savedKey: string) {
    setSaving(savedKey);
    let impact: ModelImpact | null = null;
    try {
      const res = await api.post(`/super-admin/tenants/${tenantId}/model-families/impact`, {
        model_families: buildOverrides(mutate(familiesRef.current)),
      });
      impact = res.data?.impact || null;
    } catch (err: any) {
      setSaving(null);
      antdMessage.error(t('superAdmin.modelImpact.failed')
        .replace('{error}', err?.response?.data?.error || `${err?.message || err}`));
      return;
    }
    if (impactIsEmpty(impact)) {
      persist(mutate, savedKey);
      return;
    }
    setSaving(null);
    const blocking = (impact?.blocking || 0) > 0;
    Modal.confirm({
      title: t('superAdmin.modelImpact.title'),
      width: 620,
      content: <ModelChangeImpact impact={impact as ModelImpact} />,
      okText: blocking ? t('superAdmin.modelImpact.saveAnyway') : t('superAdmin.modelImpact.save'),
      okButtonProps: blocking ? { danger: true } : undefined,
      cancelText: t('common.cancel'),
      onOk: () => persist(mutate, savedKey, blocking),
    });
  }

  function setVersion(key: string, version: string) {
    checkThenPersist(rows => rows.map(f => f.key === key
      ? { ...f, version, version_name: f.versions.find(v => v.id === version)?.name || version }
      : f), key);
  }

  function setEnabled(key: string, enabled: boolean) {
    checkThenPersist(rows => rows.map(f => f.key === key ? { ...f, enabled } : f), key);
  }

  function resetFamily(key: string) {
    checkThenPersist(rows => rows.map(f => f.key === key
      ? {
          ...f,
          version: f.platform_default_version,
          version_name: f.versions.find(v => v.id === f.platform_default_version)?.name || f.platform_default_version,
          enabled: f.platform_enabled,
        }
      : f), key);
  }

  if (loading) return <Spin />;
  if (families.length === 0) return <Empty description={t('superAdmin.modelFamilies.empty') || 'No model families configured on the platform.'} />;

  const selectableCount = families.filter(f => f.selectable).length;
  const overrideCount = families.filter(f => f.is_version_override || f.is_enabled_override).length;
  const selectableNames = selectableFamilies
    .map(k => families.find(f => f.key === k)?.name || k);

  const body = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <CascadeControls tenantId={tenantId} domain="models" cascade={cascade} onChanged={() => { load(); onCascadeChanged?.(); }} />

      {/* The reality line: exactly what the agent editor offers a user of
          this tenant. */}
      <div style={{ fontSize: 12, opacity: 0.7 }} data-testid="model-families-summary">
        {(t('superAdmin.modelFamilies.summarySelectable') ||
          '{selectable} of {total} families selectable by users · {overrides} tenant override(s)')
          .replace('{selectable}', String(selectableCount))
          .replace('{total}', String(families.length))
          .replace('{overrides}', String(overrideCount))}
      </div>
      <div style={{ fontSize: 12 }} data-testid="model-families-users-see">
        {selectableNames.length > 0
          ? (t('superAdmin.modelFamilies.usersSee') || 'Users of this tenant can pick: {list}')
              .replace('{list}', selectableNames.join(', '))
          : (t('superAdmin.modelFamilies.usersSeeNone') || 'Users of this tenant can pick no model at all.')}
      </div>

      {grouped.map(group => (
        <div key={group.provider}>
          <div
            style={{
              fontWeight: 600, fontSize: 12, marginBottom: 8,
              textTransform: 'uppercase', opacity: 0.65, letterSpacing: 0.5,
            }}
          >
            {group.provider_name}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {group.rows.map(row => {
              const hasOverride = row.is_version_override || row.is_enabled_override;
              return (
                <div
                  key={row.key}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
                    padding: '10px 12px',
                    border: '1px solid var(--border-color, rgba(148,163,184,0.25))',
                    borderRadius: 8,
                    opacity: row.enabled ? 1 : 0.65,
                  }}
                >
                  <Switch
                    size="small"
                    checked={row.enabled}
                    loading={saving === row.key}
                    onChange={(v) => setEnabled(row.key, v)}
                    data-testid={`family-switch-${row.key}`}
                  />
                  <div style={{ flex: '1 1 180px', minWidth: 140 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600 }} dir="auto">{row.name}</span>
                      {!row.platform_enabled && (
                        <Tooltip title={t('superAdmin.modelFamilies.platformOffTip') || 'Switched off platform-wide by default; this tenant overrides it.'}>
                          <Tag color="default" style={{ margin: 0, fontSize: 10 }}>
                            {t('superAdmin.modelFamilies.platformOff') || 'platform: off'}
                          </Tag>
                        </Tooltip>
                      )}
                      {hasOverride && (
                        <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>
                          {t('superAdmin.modelFamilies.overrideBadge') || 'tenant override'}
                        </Tag>
                      )}
                    </div>
                    {row.description && (
                      <div style={{ fontSize: 12, opacity: 0.7, marginTop: 2 }} dir="auto">{row.description}</div>
                    )}
                    {row.hands_over_to && (
                      <div style={{ fontSize: 12, marginTop: 2 }} data-testid={`family-hands-over-${row.key}`}>
                        {t('superAdmin.modelFamilies.handsOverTo')
                          .split('{family}').join(bidiIsolate(row.name))
                          .replace('{successor}', bidiIsolate(
                            families.find(f => f.key === row.hands_over_to)?.name || row.hands_over_to,
                          ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, opacity: 0.6 }}>
                      {t('superAdmin.modelFamilies.runsLabel') || 'Runs'}
                    </span>
                    <Select
                      size="small"
                      value={row.version}
                      style={{ minWidth: 210 }}
                      loading={saving === row.key}
                      onChange={(v) => setVersion(row.key, v)}
                      options={row.versions.filter(v => !v.legacy || v.id === row.version).map(v => ({
                        value: v.id,
                        label: v.id === row.platform_default_version
                          ? `${v.name} · ${t('superAdmin.modelFamilies.platformDefault') || 'platform default'}`
                          : v.name,
                      }))}
                    />
                    {hasOverride && (
                      <Tooltip title={t('superAdmin.modelFamilies.resetTip') || 'Revert this family to the platform defaults.'}>
                        <Button size="small" type="text" onClick={() => resetFamily(row.key)} disabled={saving === row.key}>
                          {t('superAdmin.modelFamilies.reset') || 'Reset'}
                        </Button>
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  if (bare) return body;

  return (
    <Card
      size="small"
      title={t('superAdmin.modelFamilies.cardTitle') || 'AI Models'}
      style={{ marginTop: 16 }}
    >
      <div style={{ fontSize: 12, opacity: 0.75, marginBottom: 12 }}>
        {t('superAdmin.modelFamilies.cardDescription') ||
          'Users pick a model family (Sonnet, GPT Mini…) — you decide which release each family runs and which families are available.'}
      </div>
      {body}
    </Card>
  );
}
